import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getWhatsAppQueueStatus,
  sendWhatsAppBroadcast,
  getWhatsAppBroadcastHistory,
  getWhatsAppMessageLog,
  getHubs,
  getAudiencesList,
  getPromoWhatsAppSessions,
  createPromoWhatsAppSession,
  getPromoWhatsAppSessionQr,
  logoutPromoWhatsAppSession,
  sendPromoWhatsApp,
  getPromoWhatsAppLogs,
} from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const DAILY_LIMIT = 200;
const HOURLY_LIMIT = 50;

const userData = () => JSON.parse(localStorage.getItem('userData_30jan') || '{}');

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Badge = ({ children, variant = 'default' }) => {
  const styles = {
    success: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    warning: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
    danger:  { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    info:    { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
    default: { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' },
  };
  const s = styles[variant];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 10px',
      borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {children}
    </span>
  );
};

const Alert = ({ type = 'info', children, onClose }) => {
  const isMobile = useIsMobile();
  const map = {
    info:    { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe', icon: 'ℹ️' },
    success: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0', icon: '✅' },
    warning: { bg: '#fffbeb', color: '#92400e', border: '#fde68a', icon: '⚠️' },
    danger:  { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', icon: '❌' },
  };
  const s = map[type];
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12,
    }}>
      <span>{s.icon}</span>
      <span style={{ flex: 1 }}>{children}</span>
      {onClose && (
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.color, fontSize: isMobile ? 22 : 16, lineHeight: 1, padding: 0, ...(isMobile ? { minWidth: 36, minHeight: 36 } : {}) }}>×</button>
      )}
    </div>
  );
};

const StatCard = ({ label, value, sub, color = '#1e40af', icon }) => (
  <div style={{
    background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '14px 18px', minWidth: 140, flex: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Queue Status Bar ─────────────────────────────────────────────────────────

const QueueStatusBar = ({ queueStatus, onRefresh }) => {
  if (!queueStatus) return null;
  const { sentToday = 0, queued = 0, estimatedSendTime, dailyLimit = DAILY_LIMIT, hourlyLimit = HOURLY_LIMIT } = queueStatus;
  const dailyPct = Math.min((sentToday / dailyLimit) * 100, 100);

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <StatCard label="Sent Today" value={`${sentToday} / ${dailyLimit}`} color={sentToday >= dailyLimit ? '#dc2626' : '#1e40af'} icon="📤" sub={`${dailyLimit - sentToday} remaining`} />
        <StatCard label="Queued" value={queued} color={queued > 0 ? '#d97706' : '#059669'} icon="⏳" sub={queued > 0 ? 'Pending dispatch' : 'Queue empty'} />
        <StatCard label="Hourly Limit" value={hourlyLimit} color="#7c3aed" icon="⏱️" sub="Messages per hour" />
        <StatCard label="Est. Send Time" value={estimatedSendTime || '—'} color="#0891b2" icon="🕐" sub={queued > 0 ? 'For queued messages' : 'No queue'} />
      </div>
      <div style={{ background: '#f3f4f6', borderRadius: 6, height: 6, overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 6, transition: 'width 0.4s ease',
          width: `${dailyPct}%`,
          background: dailyPct >= 100 ? '#dc2626' : dailyPct >= 80 ? '#f59e0b' : '#10b981',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: 11, color: '#6b7280' }}>Daily usage: {dailyPct.toFixed(0)}%</span>
        <button onClick={onRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#6b7280', textDecoration: 'underline' }}>Refresh</button>
      </div>
    </div>
  );
};

// ─── Broadcast Tab ────────────────────────────────────────────────────────────

const BroadcastTab = ({ queueStatus, onRefreshQueue }) => {
  const [mode, setMode] = useState('clients');
  const [message, setMessage] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [audienceId, setAudienceId] = useState('');
  const [hubs, setHubs] = useState([]);
  const [audienceOptions, setAudienceOptions] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(true);
  const [csvFile, setCsvFile] = useState(null);
  const [groupPreferred, setGroupPreferred] = useState(true);
  const [batchSize, setBatchSize] = useState(50);
  const [csvPreview, setCsvPreview] = useState(null);
  const [clientPreview, setClientPreview] = useState(null);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [alert, setAlert] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    getHubs()
      .then(res => setHubs(res?.data || []))
      .catch(() => {});
    getAudiencesList({ status: 'active' })
      .then(res => {
        const audiences = res?.data?.audiences || res?.data || [];
        setAudienceOptions(audiences.map(a => ({ id: a.id, name: a.name, member_count: a.member_count })));
      })
      .catch(() => {})
      .finally(() => setLoadingAudiences(false));
  }, []);

  const cleanHubName = (name) => (name || '').replace(/\s*hub\s*$/i, '').trim();

  const sentToday = queueStatus?.sentToday ?? 0;
  const dailyLimit = queueStatus?.dailyLimit ?? DAILY_LIMIT;
  const hourlyLimit = queueStatus?.hourlyLimit ?? HOURLY_LIMIT;
  const remaining = dailyLimit - sentToday;

  const recipientCount = mode === 'clients' ? (clientPreview?.matched ?? 0)
    : mode === 'audience' ? (audiencePreview?.matched ?? 0)
    : (csvPreview?.valid ?? 0);

  const willQueue = recipientCount > remaining && remaining >= 0;
  const queueCount = Math.max(0, recipientCount - remaining);

  const toggleHub = (hub) =>
    setSelectedHubs(prev => prev.includes(hub) ? prev.filter(h => h !== hub) : [...prev, hub]);

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').map(l => l.trim()).filter(Boolean);
      const parsed = lines.map(l => {
        const digits = l.replace(/\D/g, '');
        if (digits.length === 10) return '91' + digits;
        if (digits.length === 12 && digits.startsWith('91')) return digits;
        return null;
      });
      const valid = parsed.filter(Boolean);
      const invalid = lines.length - valid.length;
      setCsvPreview({ total: lines.length, valid: valid.length, invalid, numbers: valid, withGroup: null });
      setClientPreview(null);
      setAudiencePreview(null);
      setShowConfirm(false);
    };
    reader.readAsText(file);
  };

  const handlePreview = async () => {
    setAlert(null);
    if (!message.trim()) { setAlert({ type: 'danger', msg: 'Please enter a message before previewing.' }); return; }
    if (mode === 'csv' && !csvPreview) { setAlert({ type: 'danger', msg: 'Please upload a CSV file first.' }); return; }
    if (mode === 'audience' && !audienceId.trim()) { setAlert({ type: 'danger', msg: 'Please enter an Audience ID.' }); return; }
    setLoadingPreview(true);
    try {
      const res = await sendWhatsAppBroadcast({
        dryRun: true, mode, message,
        dateFrom, dateTo, hubs: selectedHubs,
        groupPreferred, batchSize,
        csvNumbers: csvPreview?.numbers || [],
        audienceId: mode === 'audience' ? audienceId : undefined,
      });
      if (mode === 'clients') setClientPreview(res.data);
      else if (mode === 'audience') setAudiencePreview(res.data);
      else setCsvPreview(prev => ({ ...prev, withGroup: res.data?.withGroup ?? null }));
      setShowConfirm(true);
    } catch (e) {
      setAlert({ type: 'danger', msg: e.message });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    setSending(true); setAlert(null);
    const user = userData();
    const adminName = user?.name || user?.email || 'Unknown';
    const filterCriteria = mode === 'csv'
      ? (csvFile?.name || 'CSV Upload')
      : mode === 'audience'
      ? `Audience (${audienceId})`
      : [
          dateFrom ? `From: ${dateFrom}` : null,
          dateTo ? `To: ${dateTo}` : null,
          selectedHubs.length ? `Hubs: ${selectedHubs.join(', ')}` : 'All Hubs',
        ].filter(Boolean).join(' | ');
    try {
      await sendWhatsAppBroadcast({
        dryRun: false, mode, message, dateFrom, dateTo,
        hubs: selectedHubs, groupPreferred, batchSize,
        csvNumbers: csvPreview?.numbers || [],
        audienceId: mode === 'audience' ? audienceId : undefined,
        adminName,
        filterCriteria,
      });
      setAlert({ type: 'success', msg: 'Broadcast submitted successfully. Messages are being dispatched.' });
      setShowConfirm(false); setMessage(''); setClientPreview(null); setCsvPreview(null); setAudiencePreview(null);
      setCsvFile(null); if (fileRef.current) fileRef.current.value = '';
      onRefreshQueue();
    } catch (e) {
      setAlert({ type: 'danger', msg: e.message });
    } finally {
      setSending(false);
    }
  };

  const resetMode = (m) => {
    setMode(m);
    setClientPreview(null);
    setCsvPreview(null);
    setAudiencePreview(null);
    setShowConfirm(false);
    setAlert(null);
    if (m === 'clients') setGroupPreferred(true);
  };

  return (
    <div>
      <QueueStatusBar queueStatus={queueStatus} onRefresh={onRefreshQueue} />

      {/* Mode selector */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', width: 'fit-content' }}>
        {[
          ['clients', 'Send to Current Clients'],
          ['audience', 'Send to Audience'],
          ['csv', 'Send to CSV Phone List']
        ].map(([val, label]) => (
          <button key={val} onClick={() => resetMode(val)} style={{
            padding: '9px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            background: mode === val ? '#1e40af' : 'white',
            color: mode === val ? 'white' : '#374151',
            transition: 'all 0.15s',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Filters — Clients mode */}
      {mode === 'clients' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Audience Filters</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>From Date</label>
              <input type="date" className="form-control" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>To Date</label>
              <input type="date" className="form-control" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>Hubs (leave empty for all)</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {hubs.map(hub => {
                const name = cleanHubName(hub.name);
                return (
                  <label key={hub.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                    padding: '5px 12px', borderRadius: 20, border: '1px solid',
                    borderColor: selectedHubs.includes(name) ? '#1e40af' : '#d1d5db',
                    background: selectedHubs.includes(name) ? '#eff6ff' : 'white',
                    color: selectedHubs.includes(name) ? '#1e40af' : '#374151',
                    transition: 'all 0.15s',
                  }}>
                    <input type="checkbox" checked={selectedHubs.includes(name)} onChange={() => toggleHub(name)} style={{ display: 'none' }} />
                    {selectedHubs.includes(name) ? '✓ ' : ''}{name}
                  </label>
                );
              })}
            </div>
          </div>
          {clientPreview && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13 }}>
              <strong style={{ color: '#166534' }}>{clientPreview.matched}</strong> clients matched &nbsp;·&nbsp;
              <strong style={{ color: '#1e40af' }}>{clientPreview.withGroup}</strong> have a WhatsApp group
            </div>
          )}
        </div>
      )}

      {/* Audience mode */}
      {mode === 'audience' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Target Reusable Audience</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>Select Audience</label>
              <select
                className="form-control"
                value={audienceId}
                onChange={e => setAudienceId(e.target.value)}
              >
                <option value="">{loadingAudiences ? 'Loading audiences...' : '— Select an audience —'}</option>
                {audienceOptions.map(a => (
                  <option key={a.id} value={a.id}>
                    AUD-{a.id} — {a.name} ({a.member_count?.toLocaleString() || 0} members)
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Target members snapshot or dynamic live filter from Audience.
              </div>
            </div>
          </div>
          {audiencePreview && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13 }}>
              <strong style={{ color: '#1e40af' }}>{audiencePreview.matched}</strong> members matched in Audience <strong>AUD-{audienceId}</strong>
            </div>
          )}
        </div>
      )}

      {/* CSV mode */}
      {mode === 'csv' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>CSV Upload & Options</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>Phone Number CSV</label>
              <input ref={fileRef} type="file" accept=".csv,.txt" className="form-control" onChange={handleCsvUpload} />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>One number per row only</div>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>Group Preferred</label>
              <select className="form-control" value={groupPreferred ? 'yes' : 'no'} onChange={e => setGroupPreferred(e.target.value === 'yes')}>
                <option value="yes">Yes — send to group if available</option>
                <option value="no">No — always send individually</option>
              </select>
            </div>
            {/* <div>
              <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>
                Messages in 1 Go <span style={{ color: '#9ca3af' }}>(max {hourlyLimit}/hr)</span>
              </label>
              <input type="number" className="form-control" value={batchSize} min={1} max={hourlyLimit}
                onChange={e => setBatchSize(Math.min(Math.max(1, Number(e.target.value)), hourlyLimit))} />
            </div> */}
          </div>
          {csvPreview && (
            <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Badge variant="default">Total rows: {csvPreview.total}</Badge>
              <Badge variant="success">Valid: {csvPreview.valid}</Badge>
              {csvPreview.invalid > 0 && <Badge variant="danger">Invalid (excluded): {csvPreview.invalid}</Badge>}
              {csvPreview.withGroup !== null && <Badge variant="info">With group: {csvPreview.withGroup}</Badge>}
            </div>
          )}
        </div>
      )}

      {/* Message composer */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Message</label>
        <textarea className="form-control" rows={5} value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Type your broadcast message here..." style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{message.length} characters</span>
          {message.length > 1000 && <span style={{ fontSize: 11, color: '#f59e0b' }}>Long messages may be split by WhatsApp</span>}
        </div>
      </div>

      {/* Alerts */}
      {alert && <Alert type={alert.type} onClose={() => setAlert(null)}>{alert.msg}</Alert>}

      {willQueue && !showConfirm && (
        <Alert type="warning">
          {queueCount} message{queueCount > 1 ? 's' : ''} will be queued and sent tomorrow as the daily limit has been reached.
        </Alert>
      )}

      {/* Confirm screen */}
      {showConfirm ? (
        <div style={{ background: 'white', border: '2px solid #1e40af', borderRadius: 10, padding: 20, boxShadow: '0 4px 12px rgba(30,64,175,0.1)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1e3a8a', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📋</span> Confirm Broadcast
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
            {mode === 'clients' && clientPreview && (
              <>
                <StatCard label="Matched Clients" value={clientPreview.matched} color="#1e40af" />
                {/* <StatCard label="With Group" value={clientPreview.withGroup} color="#059669" /> */}
                {/* <StatCard label="Individual" value={clientPreview.matched - clientPreview.withGroup} color="#7c3aed" /> */}
              </>
            )}
            {mode === 'csv' && csvPreview && (
              <>
                <StatCard label="Valid Numbers" value={csvPreview.valid} color="#1e40af" />
                {/* {groupPreferred && <StatCard label="With Group" value={csvPreview.withGroup ?? '—'} color="#059669" />} */}
                <StatCard label="Individual" value={groupPreferred && csvPreview.withGroup !== null ? csvPreview.valid - csvPreview.withGroup : csvPreview.valid} color="#7c3aed" />
              </>
            )}
            {willQueue && <StatCard label="Will Queue" value={queueCount} color="#d97706" />}
          </div>
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, fontSize: 13, marginBottom: 16, whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto', color: '#1e293b', lineHeight: 1.6 }}>
            {message}
          </div>
          {willQueue && (
            <Alert type="warning">{queueCount} message{queueCount > 1 ? 's' : ''} will be queued and sent tomorrow as the daily limit has been reached.</Alert>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleSend} disabled={sending} style={{
              padding: '9px 22px', background: sending ? '#9ca3af' : '#16a34a', color: 'white',
              border: 'none', borderRadius: 7, cursor: sending ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
            }}>
              {sending ? '⏳ Sending...' : '✅ Confirm & Send'}
            </button>
            <button onClick={() => setShowConfirm(false)} style={{
              padding: '9px 22px', background: 'white', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', fontWeight: 500, fontSize: 13,
            }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={handlePreview} disabled={loadingPreview} style={{
          padding: '10px 24px', background: loadingPreview ? '#9ca3af' : '#1e40af', color: 'white',
          border: 'none', borderRadius: 7, cursor: loadingPreview ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {loadingPreview ? '⏳ Loading preview...' : '👁️ Preview & Send'}
        </button>
      )}
    </div>
  );
};

// ─── Broadcast History Tab ────────────────────────────────────────────────────

const BroadcastHistoryTab = () => {
  const isMobile = useIsMobile();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedFailed, setExpandedFailed] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    getWhatsAppBroadcastHistory()
      .then(res => setHistory(res?.data || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const statusColor = (row) => {
    if (row.failedCount > 0 && row.sentCount === 0) return 'danger';
    if (row.queuedCount > 0) return 'warning';
    return 'success';
  };

  const totalPages = Math.ceil(history.length / pageSize);
  const paginated = history.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
        Every broadcast campaign is logged with admin, timestamp, filters, message, and delivery counts.
      </div>
      {error && <Alert type="danger">{error}</Alert>}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>Loading broadcast history...
          </div>
        ) : !history.length ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>No broadcast campaigns yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Date & Time', 'Admin', 'Target / CSV', 'Message', 'Total', 'Sent', 'Queued', 'Failed', 'Failed Recipients', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#374151' }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{row.adminName || '—'}</td>
                    <td style={{ padding: '10px 14px', maxWidth: 180 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }} title={row.filterCriteria || row.csvName}>
                        {row.filterCriteria || row.csvName || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 260 }}>
                      {expandedRow === i ? (
                        <div>
                          <div style={{ color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 13 }}>{row.message}</div>
                          <button onClick={() => setExpandedRow(null)} style={{ marginTop: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: isMobile ? 13 : 11, color: '#6b7280', padding: isMobile ? '6px 0' : 0, textDecoration: 'underline' }}>Show less</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151', flex: 1 }}>{row.message}</div>
                          {row.message && row.message.length > 60 && (
                            <button onClick={() => setExpandedRow(i)} style={{ flexShrink: 0, background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: isMobile ? 13 : 11, color: '#6b7280', padding: isMobile ? '6px 12px' : '1px 6px', whiteSpace: 'nowrap' }}>View</button>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, textAlign: 'center' }}>{row.totalRecipients ?? '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#059669', textAlign: 'center' }}>{row.sentCount ?? '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#d97706', textAlign: 'center' }}>{row.queuedCount ?? '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626', textAlign: 'center' }}>{row.failedCount ?? '—'}</td>
                    <td style={{ padding: '10px 14px', maxWidth: 240 }}>
                      {row.failedRecipients && row.failedRecipients.length > 0 ? (
                        <div>
                          {expandedFailed === i ? (
                            <div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                                {row.failedRecipients.map((item, idx) => (
                                  <div key={idx} style={{
                                    fontSize: 11, background: '#fee2e2',
                                    border: '1px solid #fca5a5', borderRadius: 4,
                                    padding: '4px 8px',
                                  }}>
                                    <span style={{ fontFamily: 'monospace', color: '#991b1b', fontWeight: 600 }}>
                                      {item.phone || '—'}
                                    </span>
                                    {item.error && (
                                      <span style={{ color: '#7f1d1d', marginLeft: 6 }}>
                                        · {item.error}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <button onClick={() => setExpandedFailed(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#6b7280', padding: 0, textDecoration: 'underline' }}>
                                Show less
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{
                                fontSize: 11, background: '#fee2e2',
                                border: '1px solid #fca5a5', borderRadius: 4,
                                padding: '2px 8px', overflow: 'hidden',
                              }}>
                                <span style={{ fontFamily: 'monospace', color: '#991b1b', fontWeight: 600 }}>
                                  {row.failedRecipients[0]?.phone || '—'}
                                </span>
                                {row.failedRecipients[0]?.error && (
                                  <span style={{ color: '#7f1d1d', marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    · {row.failedRecipients[0].error}
                                  </span>
                                )}
                              </div>
                              {row.failedRecipients.length > 1 && (
                                <button onClick={() => setExpandedFailed(i)} style={{
                                  flexShrink: 0, background: 'none', border: '1px solid #d1d5db',
                                  borderRadius: 4, cursor: 'pointer', fontSize: 11, color: '#6b7280',
                                  padding: '1px 6px', whiteSpace: 'nowrap',
                                }}>
                                  +{row.failedRecipients.length - 1} more
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}><Badge variant={statusColor(row)}>{statusColor(row) === 'success' ? 'Delivered' : statusColor(row) === 'warning' ? 'Partial' : 'Failed'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Showing {Math.min((page - 1) * pageSize + 1, history.length)}–{Math.min(page * pageSize, history.length)} of {history.length}
            </span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); setExpandedRow(null); setExpandedFailed(null); }}
              style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, color: '#374151', cursor: 'pointer' }}>
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="pagination-button" disabled={page === 1} onClick={() => { setPage(p => p - 1); setExpandedRow(null); setExpandedFailed(null); }}>‹ Prev</button>
            <span style={{ fontSize: 12, padding: '4px 8px' }}>Page {page} of {Math.max(totalPages, 1)}</span>
            <button className="pagination-button" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); setExpandedRow(null); setExpandedFailed(null); }}>Next ›</button>
          </div>
        </div>
      </div>  
    </div>
  );
};

// ─── Message Log Tab ──────────────────────────────────────────────────────────

const MESSAGE_TYPES = [
  'Order Confirmation', 'Wallet Recharge',
  'Draft Invoice', 'Final Invoice', 'Dispatch Notification', 'Delivery Confirmation', 'DCR Upload',
];

const MessageLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ orderId: '', eventType: '', status: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = useCallback(() => {
    setLoading(true);
    getWhatsAppMessageLog(filters)
      .then(res => setLogs(res?.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const paginated = logs.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(logs.length / pageSize);

  const statusVariant = (s) => s === 'sent' ? 'success' : s === 'failed' ? 'danger' : 'warning';
  const statusLabel = (s) => s === 'no_group' ? 'No Group' : s;

  return (
    <div>
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>Order ID</label>
            <input className="form-control" placeholder="Search order ID..." value={filters.orderId}
              onChange={e => setFilters(p => ({ ...p, orderId: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>Message Type</label>
            <select className="form-control" value={filters.eventType} onChange={e => setFilters(p => ({ ...p, eventType: e.target.value }))}>
              <option value="">All Types</option>
              {MESSAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 5 }}>Status</label>
            <select className="form-control" value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => { setPage(1); load(); }}>Apply</button>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Timestamp', 'Order ID', 'Message Type', 'Group ID', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No logs found</td></tr>
                ) : paginated.map((log, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#374151' }}>
                      {log.createdAt ? new Date(log.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>{log.orderId || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{log.eventType}</td>
                    <td style={{ padding: '10px 14px', fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>{log.chatId || '—'}</td>
                    <td style={{ padding: '10px 14px' }}><Badge variant={statusVariant(log.status)}>{statusLabel(log.status)}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Showing {Math.min((page - 1) * pageSize + 1, logs.length)}–{Math.min(page * pageSize, logs.length)} of {logs.length}</span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, color: '#374151', cursor: 'pointer' }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="pagination-button" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span style={{ fontSize: 12, padding: '4px 8px' }}>Page {page} of {Math.max(totalPages, 1)}</span>
            <button className="pagination-button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Promotional Messages Tab (Baileys, open-source sender) ─────────────────────

const cardStyle = {
  background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
  padding: 18, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const cardTitleStyle = {
  fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 12,
  textTransform: 'uppercase', letterSpacing: 0.5,
};
const PROMO_GREEN = '#16a34a';

const parsePromoList = (raw) =>
  String(raw || '')
    .split(/[\s,;\n]+/)
    .map((v) => v.trim())
    .filter(Boolean);

const PromotionalTab = () => {
  // Sessions (multiple linked WhatsApp numbers)
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [connError, setConnError] = useState(null);
  const [addingBusy, setAddingBusy] = useState(false);
  // The session currently being linked (shows its QR inline). { id, qr, status }
  const [linking, setLinking] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const sessionsPollRef = useRef(null);
  const qrPollRef = useRef(null);

  // Compose state
  const [recipientType, setRecipientType] = useState('phones'); // phones | userIds
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState(null);
  const [result, setResult] = useState(null);

  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const linkedSessions = sessions.filter((s) => s.linked);

  const loadSessions = useCallback(async () => {
    try {
      const res = await getPromoWhatsAppSessions();
      const data = res?.data || res || {};
      const list = Array.isArray(data.sessions) ? data.sessions : [];
      setSessions(list);
      setConnError(null);
      // Keep a valid selection: default to the first linked number.
      setSelectedSessionId((prev) => {
        const stillLinked = list.find((s) => s.id === prev && s.linked);
        if (stillLinked) return prev;
        const firstLinked = list.find((s) => s.linked);
        return firstLinked ? firstLinked.id : '';
      });
      return list;
    } catch (err) {
      setConnError(err.message || 'Could not reach the WhatsApp service.');
      return [];
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await getPromoWhatsAppLogs({ limit: 50 });
      const data = res?.data || res || [];
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load promotional logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    loadLogs();
  }, [loadSessions, loadLogs]);

  // Keep the session list fresh so newly-linked / dropped numbers show up.
  useEffect(() => {
    sessionsPollRef.current = setInterval(loadSessions, 6000);
    return () => {
      if (sessionsPollRef.current) clearInterval(sessionsPollRef.current);
    };
  }, [loadSessions]);

  // While a new number is being linked, poll its QR/status until it connects.
  useEffect(() => {
    if (!linking?.id) {
      if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
      return undefined;
    }
    const tick = async () => {
      try {
        const res = await getPromoWhatsAppSessionQr(linking.id);
        const data = res?.data || res || {};
        if (data.linked) {
          // Linked! Clear the QR view and refresh the list; auto-select it.
          setLinking(null);
          await loadSessions();
          setSelectedSessionId(linking.id);
          setAlert({ type: 'success', msg: `Number ${data.phone || ''} linked successfully.` });
        } else if (data.connection === 'close' && !data.hasQr) {
          // Session dropped before linking.
          setLinking(null);
          await loadSessions();
        } else {
          setLinking({ id: linking.id, qr: data.qr || null, status: data.connection || 'connecting' });
        }
      } catch (err) {
        // Session likely removed; stop showing the QR.
        setLinking(null);
      }
    };
    tick();
    qrPollRef.current = setInterval(tick, 3500);
    return () => {
      if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
    };
  }, [linking?.id, loadSessions]);

  const handleAddNumber = async () => {
    setAddingBusy(true);
    setConnError(null);
    try {
      const res = await createPromoWhatsAppSession();
      const data = res?.data || res || {};
      if (data.id) {
        setLinking({ id: data.id, qr: data.qr || null, status: data.connection || 'connecting' });
        await loadSessions();
      }
    } catch (err) {
      setConnError(err.message || 'Could not start a new connection.');
    } finally {
      setAddingBusy(false);
    }
  };

  const handleUnlink = async (sessionId) => {
    if (!window.confirm('Unlink this WhatsApp number? You will need to scan a QR again to use it.')) return;
    setActionBusyId(sessionId);
    try {
      await logoutPromoWhatsAppSession(sessionId);
      if (linking?.id === sessionId) setLinking(null);
      await loadSessions();
    } catch (err) {
      setConnError(err.message || 'Could not unlink the number.');
    } finally {
      setActionBusyId(null);
    }
  };

  const items = parsePromoList(recipientsRaw);
  const canSend = !!selectedSessionId && !sending && items.length > 0 && message.trim().length > 0;

  const handleSend = async () => {
    setAlert(null);
    setResult(null);
    if (!selectedSessionId) { setAlert({ type: 'warning', msg: 'Choose a number to send from.' }); return; }
    if (!items.length) { setAlert({ type: 'warning', msg: 'Add at least one recipient.' }); return; }
    if (!message.trim()) { setAlert({ type: 'warning', msg: 'Write a message to send.' }); return; }

    const payload = { sessionId: selectedSessionId, message: message.trim() };
    if (recipientType === 'phones') payload.phones = items;
    else payload.userIds = items;

    setSending(true);
    try {
      const res = await sendPromoWhatsApp(payload);
      const data = res?.data || res || {};
      setResult(data);
      const sent = data.sent ?? 0;
      const failed = data.failed ?? 0;
      const invalid = Array.isArray(data.invalid) ? data.invalid.length : 0;
      setAlert({
        type: failed || invalid ? 'warning' : 'success',
        msg: `Done — ${sent} sent${failed ? `, ${failed} failed` : ''}${invalid ? `, ${invalid} invalid` : ''}${data.sourcePhone ? ` · from ${data.sourcePhone}` : ''}.`,
      });
      if (sent > 0) {
        setRecipientsRaw('');
        setMessage('');
        loadLogs();
      }
    } catch (err) {
      setAlert({ type: 'danger', msg: err.message || 'Failed to send messages.' });
    } finally {
      setSending(false);
    }
  };

  const csvFileRef = useRef();

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || '');
      const rows = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

      const extracted = [];
      for (const row of rows) {
        // Take the first column; tolerate comma/semicolon/tab separated files.
        const firstCell = row.split(/[,;\t]/)[0].trim().replace(/^["']|["']$/g, '');
        if (!firstCell) continue;
        const digits = firstCell.replace(/\D/g, '');
        if (recipientType === 'phones') {
          // Accept 10-digit or 12-digit (91-prefixed) numbers.
          if (digits.length === 10) extracted.push(digits);
          else if (digits.length === 12 && digits.startsWith('91')) extracted.push(digits.slice(2));
        } else {
          if (digits.length > 0) extracted.push(digits);
        }
      }

      if (!extracted.length) {
        setAlert({ type: 'warning', msg: `No valid ${recipientType === 'phones' ? 'phone numbers' : 'user IDs'} found in the file.` });
      } else {
        // Merge with anything already typed, de-duplicating.
        const existing = parsePromoList(recipientsRaw);
        const merged = [...new Set([...existing, ...extracted])];
        setRecipientsRaw(merged.join('\n'));
        const added = merged.length - existing.length;
        setAlert({ type: 'success', msg: `Loaded ${extracted.length} value(s) from CSV — ${added} new added.` });
      }
      if (csvFileRef.current) csvFileRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
    fontFamily: 'inherit', resize: 'vertical',
  };
  const tabBtn = (active) => ({
    padding: '8px 16px', borderRadius: 8, border: `1px solid ${active ? PROMO_GREEN : '#d1d5db'}`,
    background: active ? '#f0fdf4' : 'white', color: active ? '#166534' : '#374151',
    fontWeight: 600, cursor: 'pointer', fontSize: 13,
  });

  const fmtTime = (t) => { if (!t) return '—'; try { return new Date(t).toLocaleString(); } catch { return String(t); } };

  const sessionLabel = (s) => s.phone || (s.linked ? 'Linked number' : 'Connecting…');

  return (
    <div>
      {/* Connected numbers card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={cardTitleStyle}>WhatsApp Numbers</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge variant={linkedSessions.length ? 'success' : 'default'}>
              {linkedSessions.length} connected
            </Badge>
            <button
              onClick={handleAddNumber}
              disabled={addingBusy}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: PROMO_GREEN, color: 'white', fontWeight: 700, fontSize: 13, cursor: addingBusy ? 'not-allowed' : 'pointer' }}
            >
              {addingBusy ? 'Starting…' : '+ Add number'}
            </button>
          </div>
        </div>

        {connError && <Alert type="danger" onClose={() => setConnError(null)}>{connError}</Alert>}

        {/* Inline QR panel while linking a new number */}
        {linking && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', padding: 14, border: '1px dashed #bfdbfe', background: '#f8fbff', borderRadius: 10, marginBottom: 14 }}>
            <div style={{ width: 200, height: 200, border: '1px dashed #d1d5db', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', overflow: 'hidden' }}>
              {linking.qr
                ? <img src={linking.qr} alt="WhatsApp QR code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 16 }}>Generating QR…</div>}
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontWeight: 600, color: '#111827', marginBottom: 6 }}>Link a new number</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.9 }}>
                <li>Open WhatsApp on the phone with the sending number.</li>
                <li>Go to <strong>Settings → Linked Devices → Link a Device</strong>.</li>
                <li>Scan the QR code shown here.</li>
              </ol>
              <button onClick={() => setLinking(null)} style={{ marginTop: 14, padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* List of sessions */}
        {sessions.length === 0 && !linking ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📱</div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 600, color: '#111827' }}>No numbers linked yet</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Click “Add number”, then scan the QR from WhatsApp to start sending. You can link several numbers.</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: s.linked ? '#dcfce7' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                  {s.linked ? '✅' : '⏳'}
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{sessionLabel(s)}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {s.linked ? 'Connected' : (s.connection === 'connecting' ? 'Connecting…' : 'Not connected')}
                    {s.lastError ? ` · ${s.lastError}` : ''}
                  </div>
                </div>
                {!s.linked && (
                  <button
                    onClick={() => setLinking({ id: s.id, qr: null, status: s.connection })}
                    style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #93c5fd', background: '#eff6ff', color: '#1e40af', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                  >
                    Show QR
                  </button>
                )}
                <button
                  onClick={() => handleUnlink(s.id)}
                  disabled={actionBusyId === s.id}
                  style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', color: '#991b1b', fontWeight: 600, fontSize: 12, cursor: actionBusyId === s.id ? 'not-allowed' : 'pointer' }}
                >
                  {actionBusyId === s.id ? '…' : 'Unlink'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose card */}
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Compose Promotional Message</div>
        {alert && <Alert type={alert.type} onClose={() => setAlert(null)}>{alert.msg}</Alert>}

        {/* Send-from number selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Send from</label>
          {linkedSessions.length === 0 ? (
            <div style={{ fontSize: 13, color: '#b45309' }}>No connected numbers. Add a number above to send.</div>
          ) : (
            <select
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              style={{ width: '100%', maxWidth: 320, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: 'white', cursor: 'pointer' }}
            >
              {linkedSessions.map((s) => (
                <option key={s.id} value={s.id}>{s.phone || 'Linked number'}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Send to</label>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={tabBtn(recipientType === 'phones')} onClick={() => setRecipientType('phones')}>Phone numbers</button>
            <button style={tabBtn(recipientType === 'userIds')} onClick={() => setRecipientType('userIds')}>User IDs</button>
            <span style={{ flex: 1 }} />
            <input
              ref={csvFileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleCsvUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => csvFileRef.current && csvFileRef.current.click()}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px dashed #93c5fd', background: '#eff6ff', color: '#1e40af', fontWeight: 600, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              📎 Upload CSV
            </button>
            {items.length > 0 && (
              <button
                onClick={() => setRecipientsRaw('')}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
              >
                Clear
              </button>
            )}
          </div>
          <textarea
            rows={4}
            value={recipientsRaw}
            onChange={(e) => setRecipientsRaw(e.target.value)}
            placeholder={recipientType === 'phones'
              ? 'Enter phone numbers, separated by comma, space or new line\ne.g. 9876543210, 9123456789\n\nor upload a CSV with one number per row.'
              : 'Enter user IDs, separated by comma, space or new line\ne.g. 1024, 2048\n\nor upload a CSV with one user ID per row.'}
            style={inputStyle}
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
            {items.length} recipient{items.length === 1 ? '' : 's'} detected
            <span style={{ color: '#9ca3af' }}> · CSV: first column is used, one {recipientType === 'phones' ? 'number' : 'user ID'} per row</span>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Message</label>
          <textarea
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 4096))}
            placeholder={'Write your promotional message…\nTip: use {name} to personalise when sending to user IDs.'}
            style={inputStyle}
          />
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, textAlign: 'right' }}>{message.length}/4096</div>
        </div>

        <button onClick={handleSend} disabled={!canSend} style={{ padding: '11px 22px', borderRadius: 8, border: 'none', background: canSend ? PROMO_GREEN : '#9ca3af', color: 'white', fontWeight: 700, fontSize: 14, cursor: canSend ? 'pointer' : 'not-allowed' }}>
          {sending ? 'Sending…' : `Send to ${items.length || 0} recipient${items.length === 1 ? '' : 's'}`}
        </button>
        {!selectedSessionId && <div style={{ fontSize: 12, color: '#b45309', marginTop: 10 }}>Connect and select a WhatsApp number to enable sending.</div>}

        {result?.invalid?.length > 0 && (
          <div style={{ marginTop: 16, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 6 }}>Skipped recipients</div>
            <ul style={{ margin: 0, paddingLeft: 18, color: '#6b7280' }}>
              {result.invalid.slice(0, 20).map((item, i) => (
                <li key={i}>{String(item.value)} — {item.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Recent messages */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #f3f4f6' }}>
          <span style={{ ...cardTitleStyle, marginBottom: 0 }}>Recent Messages</span>
          <button onClick={loadLogs} disabled={logsLoading} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', color: '#374151', fontWeight: 600, fontSize: 12, cursor: logsLoading ? 'not-allowed' : 'pointer' }}>
            {logsLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {(!logs || logs.length === 0) ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>No promotional messages sent yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280', background: '#f9fafb' }}>
                  <th style={{ padding: '10px 14px', fontWeight: 600 }}>Time</th>
                  <th style={{ padding: '10px 14px', fontWeight: 600 }}>From (source)</th>
                  <th style={{ padding: '10px 14px', fontWeight: 600 }}>To (phone)</th>
                  <th style={{ padding: '10px 14px', fontWeight: 600 }}>Message</th>
                  <th style={{ padding: '10px 14px', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 14px', fontWeight: 600 }}>By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtTime(log.createdAt)}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280', whiteSpace: 'nowrap' }}>{log.sourcePhone || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#111827' }}>{log.clientPhone}</td>
                    <td style={{ padding: '10px 14px', color: '#374151', maxWidth: 360 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.message}>{log.message}</div>
                      {log.status === 'failed' && log.errorMsg && (
                        <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 2 }}>{log.errorMsg}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {log.status === 'sent' ? <Badge variant="success">Sent</Badge> : <Badge variant="danger">Failed</Badge>}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#6b7280' }}>{log.adminName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  // { id: 'broadcast', label: '📢 Broadcast', desc: 'Send messages to clients or phone lists' },
  { id: 'promotional', label: '📣 Promotional Messages', desc: 'Send WhatsApp promotions directly to customers' },
  { id: 'history',   label: '📋 Broadcast Log',  desc: 'Per-campaign broadcast history' },
  { id: 'log',       label: '📄 Automated Message Log', desc: 'Individual message delivery log' },
];

const WhatsAppPortal = () => {
  const [activeTab, setActiveTab] = useState('promotional');
  const [queueStatus, setQueueStatus] = useState(null);
  const user = userData();
  const isAllowed = user.role === 'admin' || user.role === 'superAdmin' || user.access?.includes('marketing');

  const refreshQueue = useCallback(() => {
    getWhatsAppQueueStatus()
      .then(res => setQueueStatus(res?.data || null))
      .catch(() => {});
  }, []);

  useEffect(() => { if (isAllowed) refreshQueue(); }, [isAllowed, refreshQueue]);

  if (!isAllowed) {
    return (
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{
          background: 'white', border: '1px solid #fecaca', borderRadius: 12, padding: '48px 56px',
          textAlign: 'center', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🚫</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>Access Denied</div>
          <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
            You do not have permission to access the broadcast portal.
          </div>
          <div style={{ marginTop: 20 }}>
            <Badge variant="danger">Admin role required</Badge>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Page header */}
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className="page-title">💬 WhatsApp Portal</h1>
            <p className="page-subtitle">Broadcast messages and view message logs</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge variant="info">Admin Only</Badge>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 4, width: 'fit-content', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            background: activeTab === tab.id ? '#1e40af' : 'transparent',
            color: activeTab === tab.id ? 'white' : '#6b7280',
            border: 'none', cursor: 'pointer', padding: '8px 16px', borderRadius: 7,
            fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
            transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {/* Broadcast temporarily disabled — restore this line to bring it back:
      {activeTab === 'broadcast' && <BroadcastTab queueStatus={queueStatus} onRefreshQueue={refreshQueue} />} */}
      {activeTab === 'promotional' && <PromotionalTab />}
      {activeTab === 'history'   && <BroadcastHistoryTab />}
      {activeTab === 'log'       && <MessageLogTab />}
    </div>
  );
};

export default WhatsAppPortal;